import { useTranslation } from 'react-i18next';

import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import { Chip } from '@mui/material';
import type { ChipProps } from '@mui/material';

import { humanizeCode } from './format';

type ChipColor = ChipProps['color'];

/** Lifecycle status → colour + humanized label. Colour never carries meaning alone (the label is always present). */
export function CustomerStatusChip({ status }: { status?: string | null }) {
  const { t } = useTranslation('customers');
  const map: Record<string, ChipColor> = {
    ACTIVE: 'success',
    REGISTRATION_REQUIRED: 'warning',
    BLOCKED: 'error',
    DELETED: 'default',
    ANONYMIZED: 'default',
  };
  const color: ChipColor = (status ? map[status] : undefined) ?? 'default';
  return <Chip size="small" color={color} label={humanizeCode(status) || t('chips.unknown')} />;
}

export function ProfileStatusChip({ status }: { status?: string | null }) {
  if (!status) {
    return null;
  }
  const color: ChipColor = status === 'COMPLETE' ? 'success' : 'warning';
  return <Chip size="small" variant="outlined" color={color} label={humanizeCode(status)} />;
}

export function VerificationChip({ status }: { status?: string | null }) {
  const { t } = useTranslation('customers');
  const map: Record<string, ChipColor> = {
    VERIFIED: 'success',
    UNVERIFIED: 'default',
    EXPIRED: 'warning',
    REVOKED: 'error',
  };
  const color: ChipColor = (status ? map[status] : undefined) ?? 'default';
  const verified = status === 'VERIFIED';
  return (
    <Chip
      size="small"
      color={color}
      variant={verified ? 'filled' : 'outlined'}
      icon={verified ? <CheckCircleRoundedIcon /> : undefined}
      label={humanizeCode(status) || t('chips.unverified')}
    />
  );
}

/** 152-FZ consent decision → colour + label. Grant-shaped values are success, refusals/pending are muted/warning. */
export function ConsentValueChip({ value, accepted }: { value?: string | null; accepted?: boolean | null }) {
  const { t } = useTranslation('customers');
  const raw = value ?? (accepted == null ? null : accepted ? 'OPTIN_YES' : 'OPTOUT_YES');
  let color: ChipColor = 'default';
  if (raw) {
    if (raw.includes('PENDING')) {
      color = 'warning';
    } else if (raw === 'RIGHT_TO_OBJECT' || raw.endsWith('_NO') || raw.startsWith('OPTOUT')) {
      color = 'error';
    } else if (raw.includes('YES')) {
      color = 'success';
    }
  }
  return <Chip size="small" color={color} label={humanizeCode(raw) || t('chips.unknown')} />;
}

export function PrimaryChip() {
  const { t } = useTranslation('customers');
  return <Chip size="small" color="primary" variant="outlined" icon={<StarRoundedIcon />} label={t('chips.primary')} />;
}

export function DefaultChip() {
  const { t } = useTranslation('customers');
  return <Chip size="small" color="primary" variant="outlined" label={t('chips.default')} />;
}
